import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchEffectiveAppConfig } from '../services/appConfigApi';
import {
  applyAppConfigRows,
  getAppConfigRuntime,
  subscribeAppConfig,
} from '../services/appConfigRuntime';

const AppConfigContext = createContext(null);

export function AppConfigProvider({ children }) {
  const [config, setConfig] = useState(getAppConfigRuntime);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoaded(false);
      return getAppConfigRuntime();
    }
    try {
      setError('');
      const rows = await fetchEffectiveAppConfig();
      applyAppConfigRows(rows);
      const next = getAppConfigRuntime();
      setConfig(next);
      setLoaded(true);
      return next;
    } catch (e) {
      setError(e.message || 'Failed to load app config');
      setLoaded(true);
      return getAppConfigRuntime();
    }
  }, []);

  useEffect(() => {
    refresh();
    const onStorage = (e) => {
      if (e.key === 'token' || e.key === 'tenantId' || e.key === 'tenant_id') {
        refresh();
      }
    };
    window.addEventListener('storage', onStorage);
    // After login in same tab, token is set without storage event — poll lightly once
    const t = setInterval(() => {
      if (localStorage.getItem('token') && !loaded) {
        refresh();
      }
    }, 2000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(t);
    };
  }, [refresh, loaded]);

  useEffect(() => subscribeAppConfig(setConfig), []);

  const value = useMemo(
    () => ({
      config,
      loaded,
      error,
      refresh,
      dateFormat: config.DATE_FORMAT,
      currency: config.CURRENCY_CODE,
      amountFormat: config.AMOUNT_FORMAT,
      qtyFormat: config.QTY_FORMAT,
    }),
    [config, loaded, error, refresh]
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig() {
  const ctx = useContext(AppConfigContext);
  if (!ctx) {
    return {
      config: getAppConfigRuntime(),
      loaded: false,
      error: '',
      refresh: async () => getAppConfigRuntime(),
      dateFormat: getAppConfigRuntime().DATE_FORMAT,
      currency: getAppConfigRuntime().CURRENCY_CODE,
      amountFormat: getAppConfigRuntime().AMOUNT_FORMAT,
      qtyFormat: getAppConfigRuntime().QTY_FORMAT,
    };
  }
  return ctx;
}
